import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useCachedQuery, useCachedMutations } from '@/hooks/useCachedQuery'
import { useApi } from '@/hooks/useApi'
import { showToast } from '@/components/layout/Toast'
import Modal from '@/components/layout/Modal'
import { supabase } from '@/lib/supabase'
import type { Factura } from '@/lib/types'
import { formatCurrency, formatDate, r2 } from '@/lib/utils'
import { descargarCsv, type CsvColumn } from '@/lib/csv'
import { KpiRow, KpiStrip, PageHeader, Pagination, StatCard } from '@/components/ui'
import { usePagination } from '@/hooks/usePagination'
import { IconDownload, IconEye, IconEdit, IconPlus, IconSearch, IconTrash } from '@/components/icons/Icons'

const estadoBadge: Record<string, string> = {
  borrador: 'badge-borrador',
  pre_factura: 'badge-borrador',
  emitida: 'badge-enviada',
  pago_reportado: 'badge-warning',
  pagada: 'badge-aceptada',
  anulada: 'badge-rechazada',
}

const estadoLabel: Record<string, string> = {
  borrador: 'Borrador',
  pre_factura: 'Borrador',
  emitida: 'Emitida',
  pago_reportado: 'Pago Reportado',
  pagada: 'Pagada',
  anulada: 'Anulada',
}

const CRUMBS = [{ label: 'Contabilidad', to: '/contabilidad' }, { label: 'Facturas' }]

export default function Facturas() {
  const api = useApi()
  const { data: items = [], isLoading: loading } = useCachedQuery<Factura>('facturas')
  const { removeMut, updateMut } = useCachedMutations('facturas')
  const [del, setDel] = useState<Factura | null>(null)
  const [search, setSearch] = useState('')
  const [filtroEstado, setFiltroEstado] = useState<string>('todos')
  const [filtroYear, setFiltroYear] = useState<string>('todos')
  const [filtroMoneda, setFiltroMoneda] = useState<string>('todos')

  // ── Registro de un comprobante SUNAT ya emitido ──
  // NO emite ante SUNAT (eso se hace en el detalle → "Emitir a SUNAT" vía NubeFact).
  // Aquí el admin REGISTRA un comprobante ya emitido por fuera: sube el PDF + serie/número
  // → queda marcado como emitido y el cliente lo descarga de su portal.
  const [emitir, setEmitir] = useState<Factura | null>(null)
  const [tipoDoc, setTipoDoc] = useState<'01' | '03'>('01')
  const [serie, setSerie] = useState('')
  const [numero, setNumero] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [saving, setSaving] = useState(false)

  function openEmitir(f: Factura) {
    const t: '01' | '03' = f.tipo_documento_sunat === '03' ? '03' : '01'
    setTipoDoc(t)
    setSerie(t === '03' ? 'B001' : 'F001')
    setNumero('')
    setFile(null)
    setEmitir(f)
  }

  async function doEmitir() {
    if (!emitir) return
    if (!serie.trim() || !numero.trim()) { showToast('Serie y número son obligatorios', 'error'); return }
    setSaving(true)
    try {
      let pdfUrl: string | null = emitir.sunat_pdf_url ?? null
      if (file) {
        const ext = (file.name.split('.').pop() || 'pdf').toLowerCase()
        const path = `sunat/${emitir.id}/${Date.now()}.${ext}`
        const { error } = await supabase.storage.from('factura-comprobantes').upload(path, file)
        if (error) throw new Error('upload')
        pdfUrl = supabase.storage.from('factura-comprobantes').getPublicUrl(path).data.publicUrl
      }
      await updateMut.mutateAsync({
        id: emitir.id,
        tipo_documento_sunat: tipoDoc,
        sunat_serie: serie.trim().toUpperCase(),
        sunat_numero: Number(numero),
        sunat_pdf_url: pdfUrl,
        sunat_estado: 'emitida',
      })
      showToast('Comprobante registrado', 'success')
      setEmitir(null)
    } catch {
      showToast('Error al registrar el comprobante', 'error')
    } finally {
      setSaving(false)
    }
  }

  const doDelete = async () => {
    if (!del) return
    try {
      // Release tasks linked to this order before deleting
      await api.put(`/api/tareas/unlink-factura/${del.id}`, {})
      await removeMut.mutateAsync(del.id)
      showToast('Factura eliminada', 'success')
      setDel(null)
    } catch {
      showToast('Error al eliminar', 'error')
    }
  }

  const anioDe = (f: Factura) => (f.fecha_emision || '').slice(0, 4)

  const availableYears = useMemo(
    () => Array.from(new Set(items.map(anioDe).filter(Boolean))).sort((a, b) => b.localeCompare(a)),
    [items],
  )

  const monedas = useMemo(
    () => Array.from(new Set(items.map(f => f.moneda || 'PEN'))).sort(),
    [items],
  )

  const filtered = useMemo(() => {
    let result = items
    if (filtroEstado !== 'todos') result = result.filter(f => f.estado === filtroEstado)
    if (filtroYear !== 'todos') result = result.filter(f => anioDe(f) === filtroYear)
    if (filtroMoneda !== 'todos') result = result.filter(f => (f.moneda || 'PEN') === filtroMoneda)
    if (search) {
      const q = search.toLowerCase()
      result = result.filter(f =>
        [f.numero, f.cliente_nombre, f.proyecto_nombre, f.nombre_interno]
          .some(v => v?.toLowerCase().includes(q))
      )
    }
    return result
  }, [items, filtroEstado, filtroYear, filtroMoneda, search])

  const { page, setPage, totalPages, paginated, pageSize, total } =
    usePagination(filtered, 15, `${search}|${filtroEstado}|${filtroYear}|${filtroMoneda}`)

  const hayFiltros = !!search || filtroEstado !== 'todos' || filtroYear !== 'todos' || filtroMoneda !== 'todos'
  const limpiarFiltros = () => { setSearch(''); setFiltroEstado('todos'); setFiltroYear('todos'); setFiltroMoneda('todos') }

  /* ─── Totales por moneda ────────────────────────────────────────────────
     Nunca se suman PEN y USD juntos: la lista mezcla ambas y un total unico
     seria un numero inventado. `Pendiente` es lo emitido que aun no se cobro
     (emitida + pago_reportado); `borrador` no cuenta como emitido porque no
     existe ante SUNAT, pero se muestra aparte porque suele ser plata parada. */
  const totales = useMemo(() => {
    return monedas.map(moneda => {
      const delGrupo = filtered.filter(f => (f.moneda || 'PEN') === moneda)
      const suma = (estados: string[]) =>
        r2(delGrupo.filter(f => estados.includes(f.estado)).reduce((acc, f) => acc + Number(f.total || 0), 0))
      return {
        moneda,
        cantidad: delGrupo.length,
        pagado: suma(['pagada']),
        pendiente: suma(['emitida', 'pago_reportado']),
        borrador: suma(['borrador', 'pre_factura']),
      }
    }).filter(t => t.cantidad > 0)
  }, [filtered, monedas])

  /* ─── Exportar a Excel/CSV ─────────────────────────────────────────────
     Es el registro de ventas que el contador cruza con SUNAT: por eso van la
     serie/numero fiscal reales, el RUC del cliente, la base, el IGV y la
     detraccion, ademas del correlativo interno. Se exporta lo filtrado. */
  const exportarCsv = () => {
    if (filtered.length === 0) { showToast('No hay facturas que exportar', 'error'); return }

    const cols: CsvColumn<Factura>[] = [
      ['Numero Interno', f => f.numero],
      ['Serie SUNAT', f => f.sunat_serie || ''],
      ['Numero SUNAT', f => (f.sunat_numero != null ? String(f.sunat_numero).padStart(8, '0') : '')],
      ['Estado', f => estadoLabel[f.estado] || f.estado],
      ['Fecha Emision', f => f.fecha_emision || ''],
      ['Fecha Vencimiento', f => f.fecha_vencimiento || ''],
      ['Fecha Pago', f => f.fecha_pago || ''],
      ['Cliente', f => f.cliente_nombre || ''],
      ['RUC', f => f.cliente_ruc || ''],
      ['Proyecto', f => f.proyecto_nombre || ''],
      ['Concepto Interno', f => f.nombre_interno || ''],
      ['Moneda', f => f.moneda || 'PEN'],
      ['Subtotal', f => (f.subtotal ?? '')],
      ['IGV %', f => (f.igv_porcentaje ?? '')],
      ['IGV', f => (f.igv_monto ?? '')],
      ['Total', f => Number(f.total ?? 0)],
      ['Detraccion', f => (f.detraccion_aplica ? 'Si' : 'No')],
      ['Detraccion %', f => (f.detraccion_aplica ? (f.detraccion_porcentaje ?? '') : '')],
      ['Detraccion Monto PEN', f => (f.detraccion_aplica ? (f.detraccion_monto_pen ?? '') : '')],
      ['Total PEN', f => (f.total_pen ?? '')],
    ]

    const sufijo = [
      filtroYear !== 'todos' ? filtroYear : 'todas',
      filtroEstado !== 'todos' ? filtroEstado : null,
      filtroMoneda !== 'todos' ? filtroMoneda.toLowerCase() : null,
    ].filter(Boolean).join('-')

    descargarCsv(`facturas-${sufijo}`, cols, filtered)
    showToast(`${filtered.length} factura${filtered.length !== 1 ? 's' : ''} exportada${filtered.length !== 1 ? 's' : ''}`, 'success')
  }

  if (loading) return (
    <div className="page-enter">
      <PageHeader breadcrumbs={CRUMBS} />
      <div style={{ color: 'var(--text-secondary)', marginTop: '2rem', textAlign: 'center' }}>
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...
      </div>
    </div>
  )

  return (
    <div className="page-enter page-shell">
      <PageHeader
        breadcrumbs={CRUMBS}
        meta={total === items.length
          ? `${items.length} factura${items.length !== 1 ? 's' : ''}`
          : `${total} de ${items.length}`}
        actions={
          <>
            <button className="btn btn-secondary" onClick={exportarCsv} id="btn-export-facturas" title="Descarga en CSV lo que esta filtrado en pantalla">
              <IconDownload size={15} /> Exportar
            </button>
            <Link to="/facturas/nueva" className="btn btn-primary" id="btn-new-factura">
              <IconPlus size={16} /> Nueva Factura
            </Link>
          </>
        }
      />

      {totales.length > 0 && (
        <KpiStrip>
          {totales.map(t => (
            <KpiRow moneda={t.moneda} key={t.moneda}>
              <StatCard label="Cobrado" value={formatCurrency(t.pagado, t.moneda)} tone="success" />
              <StatCard label="Por cobrar" value={formatCurrency(t.pendiente, t.moneda)} tone="warning" />
              {t.borrador > 0 && (
                <StatCard label="En borrador" value={formatCurrency(t.borrador, t.moneda)} tone="danger" />
              )}
            </KpiRow>
          ))}
        </KpiStrip>
      )}

      <div className="filters-bar">
        <div className="filters-bar__search">
          <IconSearch size={14} className="filters-bar__search-icon" />
          <input className="form-input" placeholder="Buscar por N°, cliente o proyecto..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-input" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} style={{ width: 170 }}>
          <option value="todos">Todos los estados</option>
          <option value="borrador">Borrador</option>
          <option value="emitida">Emitida</option>
          <option value="pago_reportado">Pago Reportado</option>
          <option value="pagada">Pagada</option>
          <option value="anulada">Anulada</option>
        </select>
        <select className="form-input" value={filtroYear} onChange={e => setFiltroYear(e.target.value)} style={{ width: 105 }}>
          <option value="todos">Año</option>
          {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        {monedas.length > 1 && (
          <select className="form-input" value={filtroMoneda} onChange={e => setFiltroMoneda(e.target.value)} style={{ width: 110 }}>
            <option value="todos">Moneda</option>
            {monedas.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        )}
        {hayFiltros && (
          <button className="btn btn-sm btn-secondary filters-bar__clear" onClick={limpiarFiltros}>Limpiar filtros</button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="empty-state">
          <h3>Sin facturas</h3>
          <p>Crea tu primera factura.</p>
          <Link to="/facturas/nueva" className="btn btn-primary">Crear Factura</Link>
        </div>
      ) : (
        <>
        <div className="table-container page-shell__table">
          <table>
            <thead>
              <tr>
                <th>N°</th>
                <th>Cliente</th>
                <th>Proyecto</th>
                <th>Emisión</th>
                <th>Vencimiento</th>
                <th>Total</th>
                <th>Estado</th>
                <th>SUNAT</th>
                <th style={{ width: 150 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                    Sin resultados para el filtro aplicado
                  </td>
                </tr>
              ) : paginated.map(f => (

                <tr key={f.id}>
                  <td>
                    <div className="cell-stack">
                      <span className="cell-stack__title cell-date">{f.numero}</span>
                      {f.nombre_interno && <span className="cell-stack__sub">{f.nombre_interno}</span>}
                    </div>
                  </td>
                  <td>{f.cliente_nombre || '—'}</td>
                  <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.proyecto_nombre || '—'}
                  </td>
                  <td className="cell-date">{formatDate(f.fecha_emision)}</td>
                  <td className="cell-date">{formatDate(f.fecha_vencimiento)}</td>
                  <td className="cell-amount">{formatCurrency(f.total, f.moneda)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <span className={`badge ${estadoBadge[f.estado] || ''}`}>
                        {estadoLabel[f.estado] || f.estado}
                      </span>
                      {f.detraccion_aplica && (
                        <span className="badge" style={{ background: 'color-mix(in srgb, var(--danger) 15%, transparent)', color: 'var(--danger)', fontSize: '0.65rem', fontWeight: 700 }}>SPOT</span>
                      )}
                    </div>
                  </td>
                  <td className="sunat-status-col">
                    {f.sunat_estado === 'emitida' ? (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span className="sunat-badge sunat-badge--emitida">Emitida</span>
                        {f.sunat_serie && f.sunat_numero != null && (
                          <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                            {f.sunat_serie}-{String(f.sunat_numero).padStart(8, '0')}
                          </span>
                        )}
                        {f.sunat_pdf_url && (
                          <a href={f.sunat_pdf_url} target="_blank" rel="noreferrer" className="btn btn-sm btn-secondary" title="Ver PDF" style={{ padding: '2px 8px' }}>PDF</a>
                        )}
                      </div>
                    ) : f.sunat_estado === 'error' ? (
                      <span className="sunat-badge sunat-badge--error">Error</span>
                    ) : f.sunat_estado === 'emitiendo' ? (
                      <span className="sunat-badge sunat-badge--pending">Emitiendo</span>
                    ) : f.sunat_estado === 'anulada' ? (
                      <span className="sunat-badge sunat-badge--error">Anulada</span>
                    ) : (
                      <button className="btn btn-sm btn-secondary" onClick={() => openEmitir(f)} title="Registrar un comprobante SUNAT ya emitido (adjunta su PDF y serie/número). No emite ante SUNAT.">
                        Registrar
                      </button>
                    )}
                  </td>
                  <td>
                    <div className="row-actions row-actions--nowrap">
                      <Link to={`/facturas/${f.id}`} className="btn btn-sm btn-secondary" title="Ver"><IconEye size={14} /></Link>
                      <Link to={`/facturas/${f.id}/editar`} className="btn btn-sm btn-secondary" title="Editar"><IconEdit size={14} /></Link>
                      <button className="btn btn-sm btn-danger" onClick={() => setDel(f)} title="Eliminar"><IconTrash size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize}
          onPage={setPage} noun="facturas" />
        </>
      )}

      <Modal isOpen={!!emitir} onClose={() => !saving && setEmitir(null)} title={`Registrar comprobante SUNAT — ${emitir?.numero ?? ''}`}>
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px' }}>
            Registra un comprobante <strong>ya emitido</strong> (adjunta su PDF y serie/número). Esto <strong>no</strong> emite ante SUNAT — para la emisión electrónica, abre la factura y usa <strong>“Emitir a SUNAT”</strong>.
          </div>
          <div className="form-group">
            <label className="form-label">Tipo de documento</label>
            <select className="form-input" value={tipoDoc} onChange={e => {
              const t = e.target.value as '01' | '03'
              setTipoDoc(t)
              setSerie(t === '03' ? 'B001' : 'F001')
            }}>
              <option value="01">Factura (cliente con RUC)</option>
              <option value="03">Boleta (cliente sin RUC)</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div className="form-group" style={{ flex: '0 0 130px' }}>
              <label className="form-label">Serie</label>
              <input className="form-input" value={serie} onChange={e => setSerie(e.target.value)} placeholder="F001" />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Número</label>
              <input className="form-input" type="number" value={numero} onChange={e => setNumero(e.target.value)} placeholder="123" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Archivo del comprobante (PDF)</label>
            <input className="form-input" type="file" accept="application/pdf,image/*" onChange={e => setFile(e.target.files?.[0] ?? null)} />
            <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>Opcional pero recomendado: el cliente lo descargará desde su portal.</span>
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={() => setEmitir(null)} disabled={saving}>Cancelar</button>
          <button className="btn btn-primary" onClick={doEmitir} disabled={saving}>{saving ? 'Guardando…' : 'Registrar comprobante'}</button>
        </div>
      </Modal>

      <Modal isOpen={!!del} onClose={() => setDel(null)} title="Eliminar Factura">
        <p>¿Eliminar factura <strong>N° {del?.numero}</strong>?</p>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={() => setDel(null)}>Cancelar</button>
          <button className="btn btn-danger" onClick={doDelete}>Eliminar</button>
        </div>
      </Modal>
    </div>
  )
}
